import { VStack } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { BodyOf, ResponseOf } from "../../../../../core/api";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import EntityProfileAvailabilityGrid from "../../../../entityProfileModal/components/EntityProfileAvailabilityGrid";
import {
  EntityProfileErrorState,
  EntityProfileLoadingState,
  EntityProfileSaveStatus,
} from "../../../../entityProfileModal/components/EntityProfileFeedback";
import type { PatientId } from "../../../../../shared/schema/schema";

type Props = {
  patientId: PatientId;
};

type PatientAvailability = ResponseOf<"get", "./patients/:patientId/availability_settings">;
type PatientAvailabilitySettings = BodyOf<
  "put",
  "./patients/:patientId/availability_settings"
>["newSettings"];

const days = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
] as const;

export default function PatientProfileAvailabilitySection(props: Props) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const [saveStatus, setSaveStatus] = React.useState<"idle" | "saving" | "saved">("idle");
  const availabilityQueryKey = ["patientProfileModal", props.patientId, "availability"];
  const availabilityQuery = useQuery({
    queryKey: availabilityQueryKey,
    queryFn: () =>
      api.get("./patients/:patientId/availability_settings", {
        path: { patientId: props.patientId },
      }),
  });
  const timeFramesQuery = useQuery({
    queryKey: ["patientProfileModal", "availabilityTimeFrames"],
    queryFn: () => api.get("./patients/availability_time_frames", {}),
  });
  const updateAvailabilityMutation = useMutation({
    onMutate: async (newSettings) => {
      setSaveStatus("saving");
      await queryClient.cancelQueries({ queryKey: availabilityQueryKey });

      const previousAvailability =
        queryClient.getQueryData<PatientAvailability>(availabilityQueryKey);

      queryClient.setQueryData<PatientAvailability>(availabilityQueryKey, (currentAvailability) => {
        if (currentAvailability === undefined) {
          return currentAvailability;
        }

        return {
          ...currentAvailability,
          availability: newSettings,
        };
      });

      return { previousAvailability };
    },
    mutationFn: (newSettings: PatientAvailabilitySettings) =>
      api.put("./patients/:patientId/availability_settings", {
        path: { patientId: props.patientId },
        body: { newSettings },
      }),
    onSuccess: () => {
      setSaveStatus("saved");
      queryClient.invalidateQueries({ queryKey: availabilityQueryKey });
      toast({
        description: "Availability updated.",
        status: "success",
      });
    },
    onError: (_error, _newSettings, context) => {
      if (context?.previousAvailability !== undefined) {
        queryClient.setQueryData(availabilityQueryKey, context.previousAvailability);
      }

      setSaveStatus("idle");
      toast({
        description: "Could not update availability.",
        status: "error",
      });
    },
  });

  if (availabilityQuery.isError || timeFramesQuery.isError) {
    return (
      <EntityProfileErrorState
        label="Could not load availability."
        onRetry={() => {
          availabilityQuery.refetch();
          timeFramesQuery.refetch();
        }}
      />
    );
  }

  if (
    availabilityQuery.isLoading ||
    timeFramesQuery.isLoading ||
    availabilityQuery.data === undefined ||
    timeFramesQuery.data === undefined
  ) {
    return <EntityProfileLoadingState label="Loading availability" />;
  }

  return (
    <VStack alignItems="stretch" spacing={3}>
      <EntityProfileSaveStatus
        savedLabel="Availability saved"
        savingLabel="Saving availability"
        status={saveStatus}
      />
      <EntityProfileAvailabilityGrid
        availability={availabilityQuery.data.availability}
        days={days}
        isDisabled={updateAvailabilityMutation.isPending}
        timeFrames={timeFramesQuery.data.timeframes}
        onChange={(newSettings) => updateAvailabilityMutation.mutate(newSettings)}
      />
    </VStack>
  );
}
